import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { API } from '@service';
import { Loading } from '@components';
import FullScreenButton from '@components/fullScreenButton';
import {
  PDFDownload,
  PDFTitle,
} from '@components/hubModule/pdfTitleAndDownload';
import useCustomizedModuleData from '@hooks/useCustomizedModuleData';
import { FixedRatioSectionWrapper } from '@components/fixedRatioContainer';
import { injectPDFStyleOnIframeLoad } from '@utils/common';
import { INTEGRATION_TYPES } from '@model';
import alert from '@components/alert';
import HtmlStreamFrame from './HtmlStreamFrame';
import {
  getHtmlDownloadApiErrorMessageAsync,
  prepareHtmlForIframeDisplay,
  resolveHtmlDownloadResponse,
} from '@utils/htmlUploadValidation';
import { isHtmlModuleEnabled } from '@utils/htmlModuleConfig';

const FullScreenElemId = 'iframe-pdf-container';

function IntegrationIframePage({ history }) {
  const projectId = useSelector((state) => state.common.currentId);
  const [pdfContent, setPdfContent] = useState(null);
  const [imageContent, setImageContent] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [htmlLoadFailed, setHtmlLoadFailed] = useState(false);

  const url = decodeURIComponent(history.location?.search || '');
  const params = new URLSearchParams(url);
  const integrationModuleId = params.get('integrationModuleId');
  const moduleValue = params.get('moduleValue');
  const {
    targetModuleData: { link, ratio, type: moduleType },
  } = useCustomizedModuleData(moduleValue, integrationModuleId);

  const isHtmlModule = useMemo(
    () =>
      history.location.pathname.includes('htmlLink') ||
      moduleType === INTEGRATION_TYPES.HTML,
    [history.location.pathname, moduleType],
  );

  const htmlFileKey = useMemo(() => {
    if (!link) {
      return '';
    }
    return link.replace(/^https:\/\//, '');
  }, [link]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!link) {
        return;
      }

      if (isHtmlModule) {
        if (!isHtmlModuleEnabled()) {
          return;
        }
        setPdfContent(null);
        setImageContent(null);
        setHtmlContent(null);
        setHtmlLoadFailed(false);
        try {
          const res = await API.downloadHtml(htmlFileKey);
          if (cancelled) {
            return;
          }
          const resolved = await resolveHtmlDownloadResponse(res);
          if (cancelled) {
            return;
          }
          if (resolved.ok) {
            const preparedHtml = await prepareHtmlForIframeDisplay(
              resolved.htmlText,
              htmlFileKey,
              {
                assetBaseUrl: resolved.assetBaseUrl,
                downloadHtml: API.downloadHtml,
              },
            );
            if (cancelled) {
              return;
            }
            setHtmlContent(preparedHtml);
          } else {
            setHtmlLoadFailed(true);
            alert.error(resolved.error);
          }
        } catch (e) {
          if (!cancelled) {
            setHtmlLoadFailed(true);
            alert.error(await getHtmlDownloadApiErrorMessageAsync(e));
          }
        }
        return;
      }

      if (link.includes('pdf') && link.includes('integrationModuleWithTab')) {
        const { data, fileName } = await API.getPdf(link, projectId);
        if (!cancelled) {
          setPdfContent({ contentBlob: data, fileName });
        }
      }
      if (
        link.includes('.png') ||
        link.includes('.jpeg') ||
        link.includes('.jpg') ||
        link.includes('.PNG') ||
        link.includes('.JPEG') ||
        link.includes('.JPG')
      ) {
        const data = await API.downloadIMG(link.replace('https://', ''));
        if (!cancelled) {
          setImageContent(data);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [link, isHtmlModule, htmlFileKey, projectId]);

  const isImageLink =
    link &&
    (link.includes('.png') ||
      link.includes('.jpeg') ||
      link.includes('.jpg') ||
      link.includes('.PNG') ||
      link.includes('.JPEG') ||
      link.includes('.JPG'));

  const backButton = (
    <span
      className="dd-back-btn"
      onClick={() => {
        history.replace(`/project-detail/${projectId}`, {
          preventRefresh: true,
          integrationModuleId,
        });
      }}
    >
      <span className="appkiticon icon-left-chevron-fill a-font-12" /> Back
    </span>
  );

  const showHtmlIframe = Boolean(htmlContent);

  if (isHtmlModule) {
    if (!isHtmlModuleEnabled()) {
      return (
        <div className="page-wrapper integration-iframe-page integration-iframe-page--html">
          {backButton}
          <div
            className="html-module-viewport"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span>HTML module is not available in this environment.</span>
          </div>
        </div>
      );
    }

    return (
      <div className="page-wrapper integration-iframe-page integration-iframe-page--html">
        {backButton}
        <FullScreenButton elemId={`#${FullScreenElemId}`} />
        <div
          id={FullScreenElemId}
          className="html-module-viewport"
          style={{ width: '100%' }}
        >
          {showHtmlIframe ? (
            <HtmlStreamFrame
              html={htmlContent}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
              }}
            />
          ) : htmlLoadFailed ? null : (
            <Loading style={{ textAlign: 'center', marginTop: '10%' }} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper integration-iframe-page">
      {backButton}
      <FullScreenButton elemId={`#${FullScreenElemId}`} />
      <FixedRatioSectionWrapper
        ratio={ratio}
        id={FullScreenElemId}
        className={pdfContent ? 'pdf-margin' : ''}
        imgChild={isImageLink}
      >
        {link.includes('pdf') && link.includes('integrationModuleWithTab') ? (
          <div style={{ position: 'relative', height: '100%' }}>
            {pdfContent ? (
              <>
                <PDFTitle pdfContent={pdfContent} zoomed />
                <PDFDownload pdfContent={pdfContent} zoomed />
                <iframe
                  title="inte-iframe"
                  src={URL.createObjectURL(pdfContent.contentBlob)}
                  onLoad={injectPDFStyleOnIframeLoad}
                />
              </>
            ) : (
              <Loading style={{ textAlign: 'center', marginTop: '10%' }} />
            )}
          </div>
        ) : isImageLink ? (
          <>
            {imageContent ? (
              <span>
                <img
                  alt={link}
                  src={URL.createObjectURL(
                    new Blob([imageContent], {
                      type: 'image',
                    }),
                  )}
                />
              </span>
            ) : (
              <Loading style={{ textAlign: 'center', marginTop: '10%' }} />
            )}
          </>
        ) : (
          Boolean(link) && <iframe title="inte-iframe" src={link} />
        )}
      </FixedRatioSectionWrapper>
    </div>
  );
}

export default React.memo(IntegrationIframePage);
